import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ConfirmSignIn } from './pages/ConfirmSignIn.js';
import { NewProject } from './pages/NewProject.js';
import { PlatformHealth } from './pages/PlatformHealth.js';
import { PortfolioImport } from './pages/PortfolioImport.js';
import { ProjectsList } from './pages/ProjectsList.js';
import { ProjectView } from './pages/ProjectView.js';
import { SignIn } from './pages/SignIn.js';
import { StandardsQueue } from './pages/StandardsQueue.js';
import { HubHeader } from './components/HubHeader.js';
import { ChromeProvider, SurfaceProvider } from './chrome.js';

/**
 * The Hub — the GGP **staff** front end, served from `hub.staging.ggpathos.com`.
 *
 * ## Why this is a second bundle rather than a route in the Deal Analyzer
 *
 * Brad's call, 2026-07-17, for two reasons that both still hold:
 *
 * 1. **Audience break.** The Deal Analyzer (`app.`) is client-facing; the Hub is staff-only. One
 *    origin puts a client one route away from the internal tool. Auth + RLS are the real boundary,
 *    but a clean split is cheap.
 * 2. **🔴 R9 IS A LATENCY BUDGET WITH A NUMBER, NOT A SENTIMENT.** Canon rules that find/open must be
 *    *as fast or faster than browsing the X drive*, and that **any verb that loses to the old way is a
 *    DEFECT** — because that verb is where truth leaks back onto laptops. A superintendent opening a
 *    drawing on a phone in a trailer must not first download the analyzer's IRR heatmaps, category
 *    charts and sensitivity matrices. Those are `@visx` and they are the heaviest thing we ship.
 *
 * So this entry point imports **none** of the analyzer's report surface, and that omission is the
 * feature. ⚠️ Importing `AnalysisView` (or anything that pulls `@visx`) here silently re-couples the
 * bundles and spends R9's budget on a screen staff never open — the guard test
 * `HubApp.bundle.test.ts` fails if it happens.
 *
 * ## What the Hub is, per ADR-0035 §7.3
 *
 * > "Not a dashboard: **a search box, a currency badge, and a drop zone.**"
 *
 * `/projects` (find) · `/projects/new` (add) · `/projects/:code` (open, with the currency badge and
 * the drop zone) · `/standards`. Nothing else belongs here without a reason.
 *
 * ## Sign-in is shared, deliberately
 *
 * Same magic-link pages and the same session cookie as the analyzer — the cookie is scoped to
 * `.staging.ggpathos.com`, so a staff member signed in on either host is signed in on both. Identity
 * is Nexus's by ADR-0018; Magic Link is the mock until that lands. Two sign-in implementations would
 * be two things to get wrong.
 */
export function HubApp() {
  return (
    // 🔴 THE HUB'S OWN CHROME, and the reason this app stopped looking like the Deal Analyzer.
    // The shared pages used to import `AppHeader` directly, so the Hub wore the analyzer's nav —
    // including `Deals` and `+ New deal`, neither of which is a route here: both fell through the
    // catch-all below and returned the user to the page they were already on.
    <ChromeProvider value={HubHeader}>
      {/* Hub-only features on shared pages read this — ask-the-record first (ADR-0045 §4). */}
      <SurfaceProvider value="hub">
        <BrowserRouter>
          <Routes>
            <Route path="/sign-in" element={<SignIn />} />
            <Route path="/auth/confirm" element={<ConfirmSignIn />} />

            {/*
          The Hub's front door is the project list — the SEARCH BOX. The analyzer's shell (deal entry)
          is deliberately not reachable here: a staff member wanting to underwrite goes to `app.`.
        */}
            <Route path="/" element={<Navigate to="/projects" replace />} />
            <Route path="/projects" element={<ProjectsList />} />

            {/*
          `/projects/new` must not be read as a project whose CODE is "new" — React Router ranks a
          static segment above a dynamic one, so this ordering is belt-and-braces rather than load-
          bearing, but the analyzer's router carries the same note and they should not diverge.
        */}
            <Route path="/projects/new" element={<NewProject />} />
            {/*
          The tracking-log import. STAFF-ONLY BY LOCATION: it exists in the Hub and not in the
          analyzer, because GGP's whole book of work is not something a client-facing bundle should
          carry a door to.
        */}
            <Route path="/projects/import" element={<PortfolioImport />} />
            <Route path="/projects/:code" element={<ProjectView />} />

            <Route path="/standards" element={<StandardsQueue />} />

            {/*
          The platform-health panel (ADR-0064 D3). STAFF-ONLY BY LOCATION, exactly as the import above
          is: D3 rules it "staff-only first, what a client may see is a later separate ruling", and the
          analyzer bundle is client-facing — a route there would have put a page titled "Platform
          health" one URL guess away from a client, which is the ruling D3 deferred.

          ⚠️ Location is the FIRST gate, not the only one. `GET /admin/platform-health` independently
          requires the `can_see_platform_health` capability (migration 0056), so a staff member without
          a grant gets a 403 here rather than a panel. Two gates because this one is a bundle boundary
          and bundle boundaries are not access control.
        */}
            <Route path="/admin/platform-health" element={<PlatformHealth />} />

            {/* Unknown paths land on the search box, not on a dead end. */}
            <Route path="*" element={<Navigate to="/projects" replace />} />
          </Routes>
        </BrowserRouter>
      </SurfaceProvider>
    </ChromeProvider>
  );
}
